import { CustomIconPosition } from 'api/contentful/contentTypes.cloverdotcom';
import styles from './TextWithArrow.module.scss';

import cx from 'classnames';
import { TextV2, TextV2Variant } from 'components/TextV2/TextV2';
import React, { FC, HTMLAttributes } from 'react';

function getIcon(variant?: TextWithArrowProps['variant'], className?: string) {
  if (!variant) return null;
  return (
    <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" className={cx(styles.icon, className)}>
      {variant === 'arrow' && (
        <>
          <path
            d="M17.1429 8L25.1429 16L17.1429 24"
            stroke="currentColor"
            strokeLinecap="round"
            strokeWidth="2"
          />
          <path
            d="M6.85714 15C6.30486 15 5.85714 15.4477 5.85714 16C5.85714 16.5523 6.30486 17 6.85714 17V15ZM24 15H6.85714V17H24V15Z"
            fill="currentColor"
          />
        </>
      )}
      {variant === 'caret' && (
        <path
          d="M12 8.66992L20 16.6699L12 24.6699"
          stroke="currentColor"
          strokeLinecap="round"
          strokeWidth="2"
        />
      )}
      {variant === 'caretSmall' && (
        <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      )}
      {variant === 'triangle' && (
        <path
          d="M18.75 12L6.75 6V18L18.75 12Z"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.5"
        />
      )}
    </svg>
  );
}

type TextWithArrowProps = HTMLAttributes<HTMLSpanElement> & {
  size?: 'small' | 'medium' | 'large' | 'x-large';
  theme?: 'light' | 'light-purple' | 'green' | 'purple' | 'image' | 'dark-purple';
  variant?: 'arrow' | 'caretSmall' | 'caret' | 'triangle';
  cta?: boolean;
  outline?: boolean;
  textClassName?: string;
  iconClassName?: string;
  customIcon?: string;
  customIconPosition?: CustomIconPosition;
};

export const TextWithArrow: FC<TextWithArrowProps> = ({
  children,
  className,
  size = 'medium',
  theme = 'light',
  variant,
  cta,
  outline,
  textClassName,
  iconClassName,
  customIcon,
  customIconPosition = CustomIconPosition.DEFAULT,
  ...props
}) => {
  if (!variant && !cta) variant = 'arrow';
  const icon = getIcon(variant, iconClassName);
  const hasValidCustomIcon = customIcon && customIconPosition != CustomIconPosition.DEFAULT;

  return (
    <span
      className={cx(
        styles.root,
        styles[theme],
        styles[size],
        variant && styles[variant],
        cta && styles.cta,
        outline && styles.outline,
        className,
      )}
      {...props}
    >
      {(variant === 'triangle' || variant === 'caret') && icon}
      {hasValidCustomIcon && (
        <img
          src={customIcon}
          alt=""
          aria-hidden
          data-test={customIconPosition}
          className={cx(customIconPosition === CustomIconPosition.AFTER && styles.customIconAfter)}
        />
      )}
      {typeof children === 'string' && (
        <TextV2 className={cx(styles.text, textClassName)} value={children} variant={getSize(size)} />
      )}
      {variant === 'caretSmall' && icon}
      {variant === 'arrow' && icon}
    </span>
  );
};

function getSize(size: TextWithArrowProps['size']): TextV2Variant {
  switch (size) {
    case 'x-large':
      return 'modern-h4';
    case 'large':
      return 'modern-button-text-large';
    default:
      return 'modern-button-text';
  }
}
